import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { type ApiError, IndicatorCategory, type IndicatorCreate, IndicatorsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

export const useIndicatorAddForm = ({
  slug,
  onSubmit,
  onSuccess,
}: {
  slug: string;
  onSubmit?: (values: IndicatorCreate) => void;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showToast = useCustomToast();

  const mutation = useMutation({
    mutationFn: (data: IndicatorCreate) =>
      IndicatorsService.createIndicator({
        companySlug: slug,
        requestBody: data,
      }),
    onSuccess: () => {
      showToast(t('messages.toasts.success.title'), t('messages.toasts.success.indicatorCreated'), 'success');
      onSuccess?.();
    },
    onError: (err: ApiError) => {
      console.error(err);
      handleError(err, showToast);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['indicators'] });
    },
  });

  return useForm({
    defaultValues: {
      name: '',
      unit: '',
      category: 'simple' as IndicatorCategory | undefined,
      numerator: '',
      denominator_id: null as string | null,
      commitment_id: null as string | null,
      order: 999,
    },
    validators: {
      onChange: ({ value }) => ({
        fields: {
          name: value.name.length === 0 ? t('indicator.columnsHeader.indicatorName.required') : undefined,
          unit: value.unit.length === 0 ? t('indicator.columnsHeader.unit.required') : undefined,
        },
      }),
    },
    onSubmit: (values) => {
      const _values = {
        name: values.value.name,
        unit: values.value.unit,
        category: values.value.category,
        numerator: values.value.numerator ?? null,
        denominator_id: values.value.denominator_id ?? null,
        commitment_id: values.value.commitment_id ?? null,
      };
      mutation.mutate(_values);
      onSubmit?.(_values);
    },
  });
};
